import {
  Box,
  Button,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import { InView } from "react-intersection-observer";
import { Link } from "react-router-dom";
import "./App.css"; // Ensure styles are properly imported
import WarningIcon from "./components/icons/WarningIcon"; // Import the WarningIcon component
import Topbar from "./components/Topbar"; // Import the Topbar component

const sidebarList = [
  {
    title: "How It Works",
    icon: "1",
    href: "#how-it-works",
  },
  {
    title: "Review and Download Process",
    icon: "2",
    href: "#review-and-download-process",
  },
  {
    title: "Features",
    icon: "3",
    href: "#features",
  },
  {
    title: "Disclaimer",
    icon: "4",
    href: "#disclaimer",
  },
];

const ListItemIconCustom = ({ children, isActive }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      width: "20px",
      height: "20px",
      textAlign: "center",
      justifyContent: "center",
      lineHeight: "20px",
      color: isActive ? "#fff" : "#808080",
      backgroundColor: isActive ? "#F69133" : "#CCCCCC",
      fontSize: "12px",
      fontWeight: isActive ? 700 : 400,
      borderRadius: "50%",
      transition: "all 0.15 ease",
      boxShadow: isActive ? "0 0 0 3px rgba(246, 145, 51, 0.2)" : "none",
    }}
  >
    {children}
  </Box>
);

const StyledList = ({ children }) => {
  return (
    <List
      disablePadding
      sx={{
        listStyleType: "disc",
        paddingLeft: "27px",
        mt: "20px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        lineHeight: "1.4",
        "& li::marker": {
          color: "#F69133",
        },
      }}
    >
      {children}
    </List>
  );
};

const WarningBox = ({ children }) => (
  <Box
    display={"flex"}
    gap={"10px"}
    p={"20px 30px"}
    mt={"25px"}
    borderRadius={"10px"}
    border={"1px solid rgba(246, 145, 51, 1)"}
    backgroundColor={"rgba(246, 145, 51, 0.2)"}
  >
    <WarningIcon style={{ flexShrink: 0 }} />

    <Box>{children}</Box>
  </Box>
);

const HowItWorks = () => {
  const [activeSection, setActiveSection] = useState(
    sidebarList.map((item) => ({
      id: item.href.substring(1),
      inView: false,
    })),
  );

  const triggerIsInView = (inView, entry) => {
    setActiveSection((prev) => {
      const updatedSections = prev.map((section) =>
        section.id === entry.target.id ? { ...section, inView } : section,
      );

      // Only update state if there is a change
      if (JSON.stringify(prev) !== JSON.stringify(updatedSections)) {
        return updatedSections;
      }
      return prev;
    });
  };

  return (
    <>
      <Topbar />
      {/* Page Content Wrapped in Box */}
      <Box
        sx={{
          backgroundImage: { md: `url(/bg--how-it-works.svg)` },
          backgroundRepeat: "no-repeat",
          backgroundPosition: "calc(50% + 62px) 40px",
          minHeight: { md: "470px" },
        }}
      >
        <Container>
          <Box
            maxWidth={"450px"}
            mx={"auto"}
            textAlign={"center"}
            p={"90px 0 90px"}
          >
            <Typography variant="h1">How It Works</Typography>
            <Typography
              variant="body4"
              color="#8B8B8B"
              mt={1.5}
              sx={{ textWrap: "balance" }}
            >
              Learn how to clean your list in just a few simple steps.
            </Typography>

            <Link to={"/upload"}>
              <Button
                variant="contained"
                color="default"
                disableElevation
                sx={{ mt: "40px", px: "36px" }}
              >
                Upload & Clean Your List Now
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>
      <Box sx={{ backgroundColor: "#fff" }} p={"80px 0 90px"}>
        <Container maxWidth="xl">
          <Grid
            container
            sx={{ maxWidth: "1350px", margin: "auto" }}
            wrap="nowrap"
            columnGap={"24px"}
          >
            <Grid minWidth={"200px"} display={{ xs: "none", md: "block" }}>
              <List disablePadding sx={{ position: "sticky", top: "40px" }}>
                {sidebarList.map((item) => (
                  <ListItem
                    key={item.title}
                    component={"a"}
                    href={item.href}
                    sx={{
                      color: "#000",
                      px: "0",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: "42px" }}>
                      <ListItemIconCustom
                        isActive={
                          activeSection.filter((item) => item.inView)[0]?.id ===
                          item.href.substring(1)
                        }
                      >
                        {item.icon}
                      </ListItemIconCustom>
                    </ListItemIcon>
                    <ListItemText>{item.title}</ListItemText>
                  </ListItem>
                ))}
              </List>
            </Grid>

            <Grid
              maxWidth={"850px"}
              mx={"auto"}
              fontSize={"16px"}
              display={"flex"}
              flexDirection={"column"}
              gap={{ xs: "20px", md: "50px" }}
              sx={{
                "& h4": { scrollMarginTop: "40px" },
              }}
            >
              {/* How It Works Section */}
              <InView onChange={triggerIsInView} threshold={[0.05, 0.5, 1]}>
                {({ ref }) => (
                  <Box
                    ref={ref}
                    id="how-it-works"
                    sx={{ scrollMarginTop: "40px" }}
                  >
                    <Typography variant="h4" mb={"20px"}>
                      How It Works
                    </Typography>

                    <p>
                      Your list must be exported from either{" "}
                      <strong>Property Radar</strong> or{" "}
                      <strong>PropStream</strong> in its{" "}
                      <strong>original format</strong>.
                    </p>

                    <StyledList>
                      <li>
                        <strong>Property Radar Users:</strong> Export your list
                        using the <strong>"Legacy Export"</strong> option.
                        Ensure that <strong>all columns</strong> from the
                        downloaded file remain intact.
                      </li>
                      <li>
                        <strong>PropStream Users:</strong> Export the list as-is
                        and do not modify column names or structure.
                      </li>
                    </StyledList>

                    <WarningBox>
                      <strong>Important:</strong> Do not edit, rename, or modify
                      the columns before uploading. Lists must be uploaded{" "}
                      <strong>exactly as they were downloaded</strong> to be
                      processed correctly.
                    </WarningBox>
                  </Box>
                )}
              </InView>

              {/* New Section for Sample File and Payment Process */}
              <InView onChange={triggerIsInView} threshold={[0.05, 0.5, 1]}>
                {({ ref }) => (
                  <Box
                    ref={ref}
                    id="review-and-download-process"
                    sx={{ scrollMarginTop: "40px" }}
                  >
                    <Typography variant="h4" mb={"20px"}>
                      Review and Download Process
                    </Typography>
                    <StyledList>
                      <li>
                        <strong>Upload your list</strong> – Drop your file into
                        the drag & drop box.
                      </li>
                      <li>
                        <strong>Download the Sample File</strong> – Once the
                        file is cleaned, you’ll receive a{" "}
                        <strong>sample preview</strong> of the cleaned list.
                      </li>
                      <li>
                        <strong>Review the Sample File</strong> – Ensure that
                        the formatting and adjustments meet your expectations.
                      </li>
                      <li>
                        <strong>Proceed to Payment</strong> – If the sample
                        looks correct, complete the purchase.
                      </li>
                      <li>
                        <strong>Automatic Download</strong> – Upon successful
                        payment, the <strong>full cleaned list</strong> will be{" "}
                        <strong>automatically downloaded</strong> to your
                        device.
                      </li>
                    </StyledList>
                  </Box>
                )}
              </InView>

              {/* Features Section */}
              <InView onChange={triggerIsInView} threshold={[0.05, 0.5, 1]}>
                {({ ref }) => (
                  <Box ref={ref} id="features" sx={{ scrollMarginTop: "40px" }}>
                    <Typography variant="h4" mb={"20px"}>
                      Features
                    </Typography>

                    <StyledList>
                      <li>
                        <strong>Standardizes text formatting</strong> – Ensures
                        consistent capitalization and formatting for names,
                        addresses, and other key fields.
                      </li>
                      <li>
                        <strong>
                          Removes duplicate owner and address records
                        </strong>{" "}
                        – Eliminates duplicate entries so each recipient appears
                        only once.
                      </li>
                      <li>
                        <strong>Removes Corporate Entities</strong>
                      </li>
                      <li>
                        <strong>Prepares your list for direct mail</strong> –
                        Formats your data so it's ready to send straight to your
                        mail provider.
                      </li>
                      <li>
                        <strong>
                          Removes addresses on your "Exclusions List"
                        </strong>{" "}
                        – Upload your list in the Exclusions section, and the
                        list cleaner will automatically remove those addresses
                        from the cleaned file.
                      </li>
                      <li>
                        <strong>Removes unnecessary columns</strong> – Keeps
                        only the essential fields, making your list clean and
                        efficient.
                      </li>
                      <li>
                        <strong>Auto-fills missing values</strong> – Ensures key
                        fields like names and addresses are complete for a
                        polished final list.
                      </li>
                      <li>
                        <strong>Calculates offer amounts</strong> –
                        Automatically computes and formats pricing based on
                        estimated property values.
                      </li>
                      <li>
                        <strong>Supports only CSV files</strong> – Upload your
                        raw list and get back a cleaned file in the same format.
                      </li>
                      <li>
                        <strong>Easy download of cleaned files</strong> –
                        Instantly download your cleaned list, ready to use.
                      </li>
                    </StyledList>
                  </Box>
                )}
              </InView>

              {/* Disclaimer Section */}
              <InView onChange={triggerIsInView} threshold={[0.05, 0.5, 1]}>
                {({ ref }) => (
                  <Box
                    ref={ref}
                    id="disclaimer"
                    sx={{ scrollMarginTop: "40px" }}
                  >
                    <Typography variant="h4" mb={"20px"}>
                      Disclaimer
                    </Typography>
                    <p>
                      While we strive for <strong>high accuracy</strong>,
                      variations in how data is exported from Property Radar and
                      PropStream may occasionally cause minor inconsistencies.
                      We are actively working on improvements to enhance
                      accuracy over time.
                    </p>
                  </Box>
                )}
              </InView>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default HowItWorks;
